import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Skeleton } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals/ModalCDS/ModalCDS.constants';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_REQUEST_DATETIME_FORMAT } from 'constants/date';

import { openDialogAction } from 'store/global/actions';

import BulkPasteModal from './BulkPasteModal';
import InvestorConditionActions from './InvestorConditionActions';
import InvestorConditionDrawer from './InvestorConditionDrawer';
import { getConditionsTableConfig } from './InvestorConditions.config';
import { addButtonWrapperStyles } from './InvestorConditions.styles';
import { InvestorConditionsTestIds } from './InvestorConditions.testIds';

const InvestorConditionsTable = ({
  conditions = [],
  isLoading,
  isEditAllowed,
  onSaveCondition,
  onDeleteCondition,
  onMarkResponded,
  onMarkCleared,
  onBulkAddConditions,
}) => {
  const dispatch = useDispatch();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [drawerKey, setDrawerKey] = useState(0);

  const openConditionDrawer = useCallback((condition = null) => {
    const record = condition?.key ? condition : null;
    setEditingCondition(record);
    setDrawerKey(prev => prev + 1);
    setIsDrawerOpen(true);
  }, []);

  const closeConditionDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingCondition(null);
  }, []);

  const handleOpenBulkPaste = useCallback(() => {
    dispatch(
      openDialogAction({
        isRestyled: true,
        header: 'Bulk Paste Conditions',
        size: ModalSize.LARGE,
        body: dialogProps => (
          <BulkPasteModal dialogId={dialogProps.dialogId} onAddConditions={onBulkAddConditions} />
        ),
      }),
    );
  }, [dispatch, onBulkAddConditions]);

  const handleMarkResponded = useCallback(
    conditionId => {
      const today = dayjs().format(COMMON_REQUEST_DATETIME_FORMAT);
      const updated = conditions.map(condition =>
        condition.id === conditionId ? { ...condition, dateResponded: today } : condition,
      );
      onMarkResponded(updated);
    },
    [conditions, onMarkResponded],
  );

  const handleMarkCleared = useCallback(
    conditionId => {
      const today = dayjs().format(COMMON_REQUEST_DATETIME_FORMAT);
      const updated = conditions.map(condition =>
        condition.id === conditionId ? { ...condition, dateCleared: today } : condition,
      );
      onMarkCleared(updated);
    },
    [conditions, onMarkCleared],
  );

  const renderConditionActions = useCallback(
    row => (
      <InvestorConditionActions
        isEditAllowed={isEditAllowed}
        row={row}
        onDelete={onDeleteCondition}
        onEdit={openConditionDrawer}
        onMarkCleared={handleMarkCleared}
        onMarkResponded={handleMarkResponded}
      />
    ),
    [isEditAllowed, openConditionDrawer, onDeleteCondition, handleMarkResponded, handleMarkCleared],
  );

  const tableConfig = useMemo(
    () => getConditionsTableConfig({ isEditAllowed, renderActions: renderConditionActions }),
    [isEditAllowed, renderConditionActions],
  );

  const data = useMemo(
    () =>
      conditions.map((condition, index) => ({
        ...condition,
        key: condition.id ?? `condition-${index}`,
      })),
    [conditions],
  );

  if (isLoading) {
    return <Skeleton height={100} variant="rectangular" />;
  }

  return (
    <>
      {isEditAllowed && (
        <Box sx={addButtonWrapperStyles}>
          <Button
            data-testid={InvestorConditionsTestIds.ADD_CONDITION_BUTTON}
            size="small"
            startIcon={<AddBoxIcon size={IconSize[16]} />}
            onClick={openConditionDrawer}
          >
            Add Condition
          </Button>
          <Button
            data-testid={InvestorConditionsTestIds.BULK_PASTE_BUTTON}
            size="small"
            variant="line"
            onClick={handleOpenBulkPaste}
          >
            Bulk Paste
          </Button>
        </Box>
      )}
      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        data={data}
        isLoading={isLoading}
        skeletonRows={3}
      />
      <InvestorConditionDrawer
        condition={editingCondition}
        key={drawerKey}
        open={isDrawerOpen}
        onClose={closeConditionDrawer}
        onSave={onSaveCondition}
      />
    </>
  );
};

InvestorConditionsTable.propTypes = {
  conditions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      conditionText: PropTypes.string,
      dateAdded: PropTypes.string,
      dateResponded: PropTypes.string,
      dateCleared: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  isEditAllowed: PropTypes.bool,
  onSaveCondition: PropTypes.func,
  onDeleteCondition: PropTypes.func,
  onMarkResponded: PropTypes.func,
  onMarkCleared: PropTypes.func,
  onBulkAddConditions: PropTypes.func,
};

export default memo(InvestorConditionsTable);
